import clsx from 'clsx';
import styles from './styles.module.scss';
import { useOrientation } from '@/hooks';
import { useFnbBuildingOptions, useFnbFloorList } from '@/hooks/poi';
import { useTranslation } from 'react-i18next';
import { useFnbStore } from '@/stores';
import type { FloorInfo } from '@/services/dto/kiosk-config';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import ShopFnbFacilityFloorButton from '@/components/common/shop-fnb-facility/buttons/floor';
import ShopFnbFacilityBuildingDropdown from '@/components/common/shop-fnb-facility/building-dropdown';
import { AppUtils } from '@/utils/app';

const FnbSearchByFloor = () => {
    const { t } = useTranslation();
    const { isHorizontal } = useOrientation();

    // Optimized store access
    const setSelectedFloor = useFnbStore((state) => state.setSelectedFloor);
    const setSelectedBuildingId = useFnbStore((state) => state.setSelectedBuildingId);
    const updateFnbContents = useFnbStore((state) => state.updateFnbContents);
    const selectedFloor = useFnbStore((state) => state.selectedFloor);
    const selectedBuildingId = useFnbStore((state) => state.selectedBuildingId);

    // Memoized computed data
    const buildingOptions = useFnbBuildingOptions(t);
    const floorList = useFnbFloorList(selectedBuildingId, t);

    const handleSelectFloor = (floor: { id: string; label: string; value: string; data: FloorInfo | null | string[] }) => {
        setSelectedFloor(floor as any);
        updateFnbContents(SHOP_SEARCH_MODE.SEARCH_BY_FLOOR);
    };

    const handleSelectBuilding = (buildingId: string) => {
        setSelectedBuildingId(buildingId);
        handleSelectFloor(floorList[0]);
    };

    return (
        <div
            className={clsx(styles['search-by-floor'], {
                [styles['search-by-floor--vertical']]: !isHorizontal,
                ['ml-0! justify-center!']: buildingOptions.length === 1,
            })}
        >
            <div
                className={clsx(
                    styles['search-by-floor__floors'],
                    { [styles['search-by-floor__floors--vertical']]: !isHorizontal },
                    'scrollbar-custom-gray-99938f scrollbar-w-4 overflow-y-auto'
                )}
                data-region-nav
            >
                {floorList.map((floor) => {
                    return (
                        <ShopFnbFacilityFloorButton
                            tabIndex={9}
                            data-interactive
                            role="radio"
                            type="button"
                            key={floor.id}
                            aria-label={
                                floor.id === 'all'
                                    ? t('fnb.search.tts.content.all_floors')
                                    : t('fnb.search.tts.content.floor', { floor: AppUtils.getFloorTTSText(floor.label) })
                            }
                            title={floor.label}
                            aria-checked={floor.id === selectedFloor?.id}
                            onClick={() => handleSelectFloor(floor)}
                            isVertical={!isHorizontal}
                            classes={floor.id === 'all' ? 'text' : 'number'}
                        />
                    );
                })}
            </div>
            {buildingOptions.length > 1 && (
                <div className={styles['search-by-floor__dropdown']}>
                    <ShopFnbFacilityBuildingDropdown defaultValue={selectedBuildingId || 'all'} options={buildingOptions} onSelect={handleSelectBuilding} />
                </div>
            )}
        </div>
    );
};

export default FnbSearchByFloor;
